import { Component, computed, effect, inject, OnDestroy, output, signal } from '@angular/core';
import { NgbDropdownModule, NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { AbilityService, ConfirmHelperService, rxResourceFixed } from '@zayan-erp/shared';
import { TableModule } from '@openng/optimus-ui/table';
import { NotesComponent } from '../notes/notes.component';
import { Note, NotesService, RecordingToUpdate, SalesApsService } from '@zayan-erp/sales';
import { of } from 'rxjs';
import { NgClass } from '@angular/common';
import { DateFormatPipe } from '@zayan-erp/shared';
import { UserAuthenticationService } from '@zayan-erp/administration';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'lib-lot-communication',
  imports: [TableModule, NotesComponent, NgbDropdownModule, NgbTooltipModule, NgClass, DateFormatPipe],
  standalone: true,
  templateUrl: './lot-communication.component.html'
})
export class LotCommunicationComponent implements OnDestroy {
  #notesService = inject(NotesService);
  #confirmService = inject(ConfirmHelperService);
  #salesApsService = inject(SalesApsService);
  #abilityService = inject(AbilityService);
  _authService = inject(UserAuthenticationService);

  // lotId = input<string | null>(null);
  /** Lot id from input or from route context (when loaded by router). */
  effectiveLotId = computed(() => this.#salesApsService.currentDeal()?.lot.id);
  canWriteDeals = computed(() => this.#abilityService.ability.can('write-sales-deals', 'deals'));
  canLeaveTab = output<boolean>();
  noteToUpdate = signal<Note | null>(null);
  recordingToUpdate = signal<RecordingToUpdate | null>(null);

  isEdit: boolean = false;
  #toastService = inject(ToastrService);
  constructor() {
    // Watch for form changes and notes loading to restore editing state
    // This handles the case when user clicks "Stay" and component is recreated
    effect(() => {
      const formNote = this.#notesService.form.getRawValue().note || this.#notesService.form.getRawValue().s3DocumentId;
      const loadedNotes = this.notes();

      // Only restore if form has a note value, notes are loaded, and we're not already editing
      if (formNote && loadedNotes.length > 0 && !this.noteToUpdate()) {
        const matchingNote = loadedNotes.find(note => note?.note === formNote);
        if (matchingNote) {
          // Restore the editing state without calling editNote (which would scroll)
          this.noteToUpdate.set(matchingNote);
          this.recordingToUpdate.set({
            url: matchingNote?.voiceNoteUrl ?? null,
            documentId: matchingNote?.voiceNoteId ?? null
          });
          // Form is already patched with values from the previous state
        }
      }
    });
  }

  openLotCommModal() {
    setTimeout(() => {
      const notesForm = document.querySelector('lib-notes');
      if (notesForm) {
        notesForm.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }, 0);
  }

  getNotes = rxResourceFixed({
    params: () => ({ lotId: this.effectiveLotId() }),
    stream: ({ params }) => {
      if (!params.lotId) {
        return of([]);
      }
      const notes = this.#notesService.readNotes(params.lotId);
      return notes;
    }
  });

  notes = computed(() => this.getNotes.value() ?? []);
  saveNote() {
    if (!this.canWriteDeals()) return;
    this.#notesService.form.markAllAsTouched();
    if (this.#notesService.form.invalid) {
      return;
    }
    if (this.noteToUpdate()) {
      this.#notesService
        .updateNote(this.effectiveLotId()!, this.noteToUpdate()!.id, this.#notesService.form.getRawValue())
        .subscribe(() => {
          this.resetForm();
        });
      this.noteToUpdate.set(null);
    } else {
      this.#notesService.createNote(this.effectiveLotId()!, this.#notesService.form.getRawValue()).subscribe(() => {
        this.resetForm();
      });
    }
  }

  deleteNote(note: Note) {
    if (!this.canWriteDeals()) return;
    if (!note?.id) {
      return;
    }
    this.#notesService.deleteNote(this.effectiveLotId()!, note.id).subscribe({
      next: () => {
        this.getNotes.reload();
      },
      error: error => {
        console.error('Error deleting note:', error);
      }
    });
  }

  deleteNoteConfirm(note: Note) {
    if (!this.canWriteDeals()) return;
    this.noteToUpdate.set(note);
    this.#confirmService
      .showModal({
        icon: 'confirm-icon',
        header: 'Delete Note',
        message: 'Are you sure you want to delete this note?',
        acceptLabel: 'Delete',
        acceptSeverity: 'danger',
        rejectLabel: 'Cancel'
      })
      .subscribe(confirmed => {
        if (confirmed) {
          this.deleteNote(note);
          this.noteToUpdate.set(null);
        } else {
          this.noteToUpdate.set(null);
          this.recordingToUpdate.set(null);
        }
      });
  }

  editNote(note: Note) {
    if (!this.canWriteDeals()) return;
    this.noteToUpdate.set(note);
    this.#notesService.form.patchValue({
      noteTypeId: note?.noteTypeId,
      note: note?.note,
      s3DocumentId: note?.voiceNoteId
    });
    this.recordingToUpdate.set({ url: note?.voiceNoteUrl, documentId: note?.voiceNoteId });
    this.openLotCommModal();
  }

  resetForm() {
    this.getNotes.reload();
    const selectedNoteTypeId = this.#notesService.form.get('noteTypeId')?.value ?? null;
    this.#notesService.form.reset({
      noteTypeId: selectedNoteTypeId,
      note: null,
      s3DocumentId: null
    });
    this.noteToUpdate.set(null);
    this.recordingToUpdate.set(null);
  }

  ngOnDestroy(): void {
    this.getNotes.reload();
  }
}
